/**
 * Modo de renderizado: DIAGNOSTIC (dcm 16-bit) vs PREVIEW (jpg/webp).
 * Persistencia solo de toggles explícitos; default según device + contenido manifest.
 */
import { isConstrainedUiDevice } from './mobileDownloadProfile'
import { inspectManifestMedia, normalizeManifestShape } from './renderModeUrls'

export const RENDER_MODE = {
  DIAGNOSTIC: 'DIAGNOSTIC',
  PREVIEW: 'PREVIEW',
}

export const RENDER_MODE_SESSION_KEY = 'udv.renderMode'
/** Solo true si el usuario tocó el toggle (no defaults auto). */
export const RENDER_MODE_USER_SET_KEY = 'udv.renderMode.userSet'

export const DIAGNOSTIC_TOOLS_TOOLTIP =
  'Cargando imagen diagnóstica para medir…'

export const PIXEL_ACCURATE_NO_DICOM_TOOLTIP =
  'Este frame no tiene DICOM disponible para medición'

const HEAVY_CINE_MODS = new Set(['XA', 'XRF', 'RF'])

/**
 * ¿El manifest tiene series de cine pesado (XA/XRF/RF)?
 * @param {object|null|undefined} rawManifest
 * @returns {boolean}
 */
export function manifestHasHeavyCineSeries(rawManifest) {
  const shaped = normalizeManifestShape(rawManifest)
  const series = shaped && Array.isArray(shaped.series) ? shaped.series : []
  for (let i = 0; i < series.length; i++) {
    const s = series[i]
    if (!s) continue
    const mod = String(s.modality || s.Modality || '')
      .trim()
      .toUpperCase()
    if (HEAVY_CINE_MODS.has(mod)) return true
    const insts = Array.isArray(s.instances) ? s.instances : []
    for (let j = 0; j < insts.length; j++) {
      const inst = insts[j]
      if (!inst) continue
      const im = String(inst.modality || inst.Modality || '')
        .trim()
        .toUpperCase()
      if (HEAVY_CINE_MODS.has(im)) return true
      const nf = Number(inst.numberOfFrames)
      if (Number.isFinite(nf) && nf > 1 && Number(inst.byteLength) >= 8 * 1024 * 1024) {
        return true
      }
    }
  }
  return false
}

/**
 * Tools que necesitan calibración médica / píxeles reales (→ upgrade frame a DICOM).
 * Wwwc, Magnify, Pan, Zoom e Invert se permiten sobre preview.
 */
export const PIXEL_ACCURATE_TOOLS = new Set([
  'Probe',
  'Length',
  'Angle',
  'CobbAngle',
  'EllipticalRoi',
  'RectangleRoi',
  'FreehandRoi',
])

/** Alias legacy: solo tools pixel-accurate (no bloquear Wwwc/Magnify). */
export const DIAGNOSTIC_ONLY_TOOLS = PIXEL_ACCURATE_TOOLS

/**
 * @param {string} tool
 * @returns {boolean}
 */
export function isPixelAccurateTool(tool) {
  return PIXEL_ACCURATE_TOOLS.has(tool)
}

/**
 * @param {string} tool
 * @returns {boolean}
 * @deprecated Prefer isPixelAccurateTool — ya no bloquea WW/WC.
 */
export function isDiagnosticOnlyTool(tool) {
  return isPixelAccurateTool(tool)
}

/**
 * En PREVIEW todas las tools de UI están “usables”:
 * las pixel-accurate disparan upgrade on-demand en App (no hard-block).
 * @param {string} tool
 * @param {string} renderMode
 */
export function canUseToolInRenderMode(tool, renderMode) {
  if (!tool || tool === 'notool') return true
  return true
}

/**
 * @param {string} [mode]
 * @returns {boolean}
 */
export function isValidRenderMode(mode) {
  return mode === RENDER_MODE.DIAGNOSTIC || mode === RENDER_MODE.PREVIEW
}

/**
 * Prefer PREVIEW en phone/tablet o red lenta (cuando hay ambas medias).
 * @returns {boolean}
 */
export function prefersPreviewDevice() {
  try {
    if (isConstrainedUiDevice()) return true
  } catch (e) {
    /* ignore */
  }
  try {
    if (typeof navigator !== 'undefined' && navigator.connection) {
      const c = navigator.connection
      if (c.saveData) return true
      const et = String(c.effectiveType || '').toLowerCase()
      if (et === 'slow-2g' || et === '2g') return true
    }
  } catch (e) {
    /* ignore */
  }
  return false
}

/**
 * Default sin mirar manifest (fallback).
 * @returns {'DIAGNOSTIC'|'PREVIEW'}
 */
export function getDefaultRenderMode() {
  return prefersPreviewDevice() ? RENDER_MODE.PREVIEW : RENDER_MODE.DIAGNOSTIC
}

/**
 * @returns {boolean}
 */
export function isRenderModeUserSet() {
  try {
    if (typeof sessionStorage !== 'undefined') {
      return sessionStorage.getItem(RENDER_MODE_USER_SET_KEY) === '1'
    }
  } catch (e) {
    /* ignore */
  }
  return false
}

/**
 * @param {boolean} userSet
 */
export function setRenderModeUserSet(userSet) {
  try {
    if (typeof sessionStorage === 'undefined') return
    if (userSet) sessionStorage.setItem(RENDER_MODE_USER_SET_KEY, '1')
    else sessionStorage.removeItem(RENDER_MODE_USER_SET_KEY)
  } catch (e) {
    /* ignore */
  }
}

/**
 * Lee modo de sesión si existe (no default).
 * @returns {'DIAGNOSTIC'|'PREVIEW'|null}
 */
export function readStoredRenderModeOnly() {
  try {
    if (typeof sessionStorage !== 'undefined') {
      const v = sessionStorage.getItem(RENDER_MODE_SESSION_KEY)
      if (isValidRenderMode(v)) return v
    }
  } catch (e) {
    /* ignore */
  }
  return null
}

/**
 * Lee sesión o default device (sin inspeccionar manifest).
 * @returns {'DIAGNOSTIC'|'PREVIEW'}
 */
export function readStoredRenderMode() {
  const stored = readStoredRenderModeOnly()
  if (stored) return stored
  return getDefaultRenderMode()
}

/**
 * @param {'DIAGNOSTIC'|'PREVIEW'} mode
 * @param {{ userSet?: boolean }} [opts]
 */
export function persistRenderMode(mode, opts = {}) {
  if (!isValidRenderMode(mode)) return
  try {
    if (typeof sessionStorage !== 'undefined') {
      sessionStorage.setItem(RENDER_MODE_SESSION_KEY, mode)
    }
  } catch (e) {
    /* ignore */
  }
  if (opts.userSet) {
    setRenderModeUserSet(true)
  }
}

/**
 * ¿El modo es viable para las caps del manifest?
 * @param {'DIAGNOSTIC'|'PREVIEW'} mode
 * @param {{ hasPreview: boolean, hasDicom: boolean }} caps
 */
export function isModeViableForCaps(mode, caps) {
  if (!caps) return true
  if (mode === RENDER_MODE.PREVIEW) {
    // PREVIEW “real” necesita previews; si no hay, no es viable (caer a DIAG)
    return !!caps.hasPreview
  }
  if (mode === RENDER_MODE.DIAGNOSTIC) {
    // Sin dicom solo previews → DIAG no viable
    return !!caps.hasDicom
  }
  return true
}

/**
 * Elige modo para un manifest.
 * @param {object} rawManifest
 * @param {{
 *   assetBaseUrl?: string|null,
 *   urlMode?: string|null,
 *   honorUserSet?: boolean,
 *   pacsDefaultMode?: 'PREVIEW'|'DIAGNOSTIC'|null,
 *   portalProfile?: boolean,
 * }} [opts]
 * @returns {{
 *   mode: 'DIAGNOSTIC'|'PREVIEW',
 *   caps: object,
 *   source: 'url'|'user'|'caps'|'pacs'|'portal'|'device',
 *   clearedUserSet: boolean,
 * }}
 */
export function pickRenderModeForManifest(rawManifest, opts = {}) {
  const assetBaseUrl = opts.assetBaseUrl != null ? opts.assetBaseUrl : null
  const honorUserSet = opts.honorUserSet !== false
  const archiveUrlOverride =
    opts.archiveUrlOverride != null && String(opts.archiveUrlOverride).trim()
      ? String(opts.archiveUrlOverride).trim()
      : null
  const caps = inspectManifestMedia(rawManifest, assetBaseUrl, archiveUrlOverride)

  // 1) URL override
  if (opts.urlMode && isValidRenderMode(opts.urlMode)) {
    let mode = opts.urlMode
    if (!isModeViableForCaps(mode, caps)) {
      mode = caps.hasDicom
        ? RENDER_MODE.DIAGNOSTIC
        : caps.hasPreview
          ? RENDER_MODE.PREVIEW
          : RENDER_MODE.DIAGNOSTIC
    }
    return { mode, caps, source: 'url', clearedUserSet: false }
  }

  // 2) Solo un tipo de media → forzar
  if (caps.hasDicom && !caps.hasPreview) {
    const cleared = honorUserSet && isRenderModeUserSet()
    if (cleared) setRenderModeUserSet(false)
    return {
      mode: RENDER_MODE.DIAGNOSTIC,
      caps,
      source: 'caps',
      clearedUserSet: !!cleared,
    }
  }
  if (caps.hasPreview && !caps.hasDicom) {
    const cleared = honorUserSet && isRenderModeUserSet()
    if (cleared) setRenderModeUserSet(false)
    return {
      mode: RENDER_MODE.PREVIEW,
      caps,
      source: 'caps',
      clearedUserSet: !!cleared,
    }
  }

  // 3) Sin nada usable
  if (!caps.hasDicom && !caps.hasPreview) {
    return {
      mode: RENDER_MODE.DIAGNOSTIC,
      caps,
      source: 'caps',
      clearedUserSet: false,
    }
  }

  // 4) Portal paciente: PREVIEW estricto (ignora userSet DIAG)
  if (
    opts.portalProfile &&
    caps.hasPreview &&
    isModeViableForCaps(RENDER_MODE.PREVIEW, caps)
  ) {
    let clearedUserSet = false
    if (honorUserSet && isRenderModeUserSet()) {
      const stored = readStoredRenderModeOnly()
      if (stored === RENDER_MODE.DIAGNOSTIC) {
        setRenderModeUserSet(false)
        clearedUserSet = true
      }
    }
    return {
      mode: RENDER_MODE.PREVIEW,
      caps,
      source: 'portal',
      clearedUserSet,
    }
  }

  // 5) Ambos: honor userSet viable (planes clínicos)
  if (honorUserSet && isRenderModeUserSet()) {
    const stored = readStoredRenderModeOnly()
    if (stored && isModeViableForCaps(stored, caps)) {
      return { mode: stored, caps, source: 'user', clearedUserSet: false }
    }
    // userSet inviable para este estudio
    setRenderModeUserSet(false)
  }

  // 6) DEFAULT_RENDER_MODE del PACS (si viable)
  const pacsMode = opts.pacsDefaultMode
  if (pacsMode && isValidRenderMode(pacsMode) && isModeViableForCaps(pacsMode, caps)) {
    return { mode: pacsMode, caps, source: 'pacs', clearedUserSet: false }
  }

  // 7) Ambos + device/red (clínico)
  const mode = prefersPreviewDevice() ? RENDER_MODE.PREVIEW : RENDER_MODE.DIAGNOSTIC
  return { mode, caps, source: 'device', clearedUserSet: false }
}

/**
 * Helper: caps del manifest ya normalizado o no.
 */
export function getManifestCaps(rawManifest, assetBaseUrl) {
  return inspectManifestMedia(
    normalizeManifestShape(rawManifest),
    assetBaseUrl || null
  )
}

/**
 * Normaliza entrada de UI / URL.
 * @param {string} raw
 * @returns {'DIAGNOSTIC'|'PREVIEW'|null}
 */
export function parseRenderMode(raw) {
  if (raw == null) return null
  const s = String(raw).trim().toUpperCase()
  if (s === 'DIAGNOSTIC' || s === 'DIAG' || s === 'D') return RENDER_MODE.DIAGNOSTIC
  if (s === 'PREVIEW' || s === 'NONDIAGNOSTIC' || s === 'NO_DIAGNOSTIC' || s === 'P') {
    return RENDER_MODE.PREVIEW
  }
  return null
}
